/**
 * Touch-driven swipe-to-reveal hook.
 *
 * Wraps a row element. Listens to touchstart/move/end and progressively
 * translates a "front" element when the user swipes horizontally past a
 * dead-zone. Direction is RTL-aware: swipe left in LTR (or swipe right in
 * RTL) reveals the trailing-side action.
 *
 * Calls `onTrigger` exactly once when the swipe crosses the trigger
 * threshold AND the user lifts their finger. The caller decides what to
 * do (typically: show a confirm modal that calls `reset()` on cancel).
 *
 * Returns `{ ref, offset, reset }` so the caller can apply CSS:
 *   <li ref={swipe.ref} style={{ transform: `translateX(${swipe.offset}px)` }}>
 *
 * The hook intentionally doesn't render anything — pure behavior. UI lives
 * in the caller.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const DEAD_ZONE  = 12;   // px before any movement is applied
const TRIGGER    = 80;   // px past dead-zone to fire onTrigger
const REVEAL_CAP = 120;  // max visible offset

function isRtl() {
  return typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
}

export function useSwipeToReveal({ onTrigger }) {
  const ref = useRef(null);
  const startXRef = useRef(null);
  const triggeredRef = useRef(false);
  const [offset, setOffset] = useState(0);

  const reset = useCallback(() => {
    setOffset(0);
    triggeredRef.current = false;
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const onTouchStart = (e) => {
      startXRef.current = e.touches?.[0]?.clientX ?? null;
      triggeredRef.current = false;
    };
    const onTouchMove = (e) => {
      if (startXRef.current == null) return;
      const x = e.touches?.[0]?.clientX ?? null;
      if (x == null) return;
      const raw = x - startXRef.current;
      // Direction: in LTR, swipe-left (raw<0) reveals trailing action.
      // In RTL, mirror so swipe-right (raw>0) does the same job visually.
      const dir = isRtl() ? +1 : -1;
      const progress = dir * raw;
      if (progress <= DEAD_ZONE) { setOffset(0); return; }
      const visible = Math.min(progress - DEAD_ZONE, REVEAL_CAP);
      setOffset(dir * visible);
    };
    const onTouchEnd = () => {
      const progress = Math.abs(offset);
      if (progress > TRIGGER && !triggeredRef.current) {
        triggeredRef.current = true;
        try { onTrigger?.(); } catch (_) { /* ignore */ }
      } else {
        setOffset(0);
      }
      startXRef.current = null;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove',  onTouchMove,  { passive: true });
    el.addEventListener('touchend',   onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove',  onTouchMove);
      el.removeEventListener('touchend',   onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [offset, onTrigger]);

  return { ref, offset, reset };
}

export default useSwipeToReveal;
